import { Row, Col, Container } from 'react-bootstrap';
import translationFallback from '@/utilites/translations/translationFallback';
import { useTranslation } from 'next-i18next';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { OtherEnum } from '@/types/translations/OtherEnum';
import Head from 'next/head';
import BreadcrumbStructuredData from '@/components/structured_data/BreadcrumbStructuredData';
import { useRouter } from 'next/router';
import getBasicStaticSideProps from '@/lib/staticSideProps/getBasicStaticSideProps';
import getCanonicalUrl from '@/utilites/seo/get-canonical-url';
import LanguageAlternates from '@/components/seo/LanguageAlternates';

export default function Imprint() {
  const { translate } = useTypedTranslation<OtherEnum>('other');
  const { t: tCommon } = useTranslation('common');
  const router = useRouter();
  const canonicalUrl = getCanonicalUrl(router);

  return (
    <>
      <Head>
        <title>{translate(OtherEnum['imprint.meta_title'])}</title>
        <link rel="canonical" href={canonicalUrl} />
        <LanguageAlternates />
        <BreadcrumbStructuredData
          items={[
            { name: 'Home', item: 'https://spiritory.com' },
            { name: translate(OtherEnum['imprint.imprint']), item: canonicalUrl },
          ]}
        />
      </Head>
      <Container className="p-5">
        <Row
          className="bg-primary text-white rounded"
          style={{
            width: '60%',
            minWidth: 900,
            padding: '1.5rem 4rem',
            margin: '0 auto',
          }}
        >
          <Row>
            <Col className="d-flex justify-content-center align-items-center">
              <h1>{translate(OtherEnum['imprint.imprint'])}</h1>
            </Col>
          </Row>
          <Row>
            <Col className="d-flex flex-column gap-3">
              <div>
                <strong style={{ fontWeight: 700, fontSize: '1.5rem' }}>
                  {translate(OtherEnum['imprint.operator_of_website'])}:
                </strong>
              </div>
              <div className="d-flex flex-column gap-2">
                <strong style={{ fontSize: '1.1rem' }}>SPIRITORY GmbH</strong>
                <div className="d-flex flex-column gap-1">
                  <address style={{ margin: 0 }}>
                    Oberländerstr. 33
                    <br />
                    81371{' '}
                    {translationFallback('munich', tCommon('munich'), 'Munich')}
                    <br />
                    {translate(OtherEnum['imprint.germany'])}
                  </address>
                  <p className="text-white text-decoration-none">
                    {translate(OtherEnum['imprint.phone_number'])}: +49
                    15739597262
                  </p>
                  <a
                    className="text-white text-decoration-none"
                    href="mailto:help@spiritory.com"
                  >
                    {translate(OtherEnum['imprint.email'])}: help@spiritory.com
                  </a>
                </div>
              </div>
              <div className="d-flex flex-column gap-2">
                <strong style={{ fontSize: '1.1rem' }}>
                  {translate(OtherEnum['imprint.managing_partners'])}:
                </strong>
                <p>Janis Wilczura</p>
              </div>
              <div className="d-flex flex-column gap-2">
                <strong style={{ fontSize: '1.1rem' }}>
                  {translate(OtherEnum['imprint.register_court'])}:
                </strong>
                <p>{translate(OtherEnum['imprint.local_court_of_munich'])}</p>
              </div>
              <div className="d-flex flex-column gap-2">
                <strong style={{ fontSize: '1.1rem' }}>
                  {translate(OtherEnum['imprint.registration_number'])}:
                </strong>
                <p>HRB 274766</p>
              </div>
              <div className="d-flex flex-column gap-2">
                <strong style={{ fontSize: '1.1rem' }}>
                  {translate(OtherEnum['imprint.vat_identification'])}:
                </strong>
                <p>DE352813624</p>
              </div>
              <div className="d-flex flex-column gap-2">
                <strong style={{ fontSize: '1.1rem' }}>
                  {translate(OtherEnum['imprint.responsible_of_content'])}
                </strong>
                <p>
                  Janis Wilczura, {translate(OtherEnum['imprint.address_as'])}{' '}
                  SPIRITORY GmbH
                </p>
              </div>
              <div className="d-flex flex-column gap-2">
                <strong style={{ fontSize: '1.1rem' }}>
                  {translate(OtherEnum['imprint.online_dispute_resolution'])}:
                </strong>
                <p>
                  {translate(
                    OtherEnum['imprint.online_dispute_resolution_subtitle']
                  )}{' '}
                  <a
                    rel="noreferrer"
                    className="text-white"
                    target="_blank"
                    href="https://ec.europa.eu/consumers/odr."
                  >
                    https://ec.europa.eu/consumers/odr.
                  </a>
                  <br />
                  {translationFallback(
                    'imprint.online_dispute_resolution_2',
                    'imprint.online_dispute_resolution_2',
                    'We are neither willing nor obliged to participate in a dispute resolution procedure before a consumer arbitration board.'
                  )}
                  <br />
                  {translate(OtherEnum['imprint.this_imprint_applies'])}.
                </p>
              </div>
              <div>
                <p>{translate(OtherEnum['imprint.photos_credit_final'])}</p>
              </div>
            </Col>
          </Row>
        </Row>
      </Container>
    </>
  );
}

export const getStaticProps = getBasicStaticSideProps([
  'common',
  'other',
  'index',
]);
